import { useEffect, useRef, useState } from 'react';
import { observationService } from '../services/observationService';
import { DataTypesEnum } from '../data/enums';
import { useObservationField } from './useObservationField';
import {
  ObservationDraftKeyProps,
  observationDraftService,
} from '../services/observationDraftService';
import { cacheInvalidationService } from '../services/cacheInvalidationService';

export interface ObservationValueProps<T> extends ObservationDraftKeyProps {
  /** Pure display seed shown when nothing is persisted yet. NEVER written to
   * storage by itself, in either mode — matches plain HTML/React defaultValue
   * conventions. Screens that need the value durable must still commit it. */
  defaultValue?: T;
  onDefaultLoad?: (value: T) => void;
  /** Defaults to identity for string T. */
  serialize?: (value: T) => string;
  /** Defaults to identity for string T. */
  deserialize?: (raw: string) => T;
}

export interface ObservationValueBinding<T> {
  value: T;
  /** True once the persisted (or default) value has been loaded. */
  ready: boolean;
  /** True when this field is bound to the draft layer (screen is scoped and the
   * observable isn't a collection). Exposed for the rare atom that needs to
   * branch on persistence timing itself (see RichTextField, which stays on the
   * lower-level useObservationField instead of this hook). */
  isDraft: boolean;
  /** Update the value. Draft mode: writes through immediately (debounced flush).
   * Legacy mode: local state only - call commit() to persist. */
  setValue: (value: T) => void;
  /** Persist `value` now. Draft mode: writes it into the draft store (idempotent
   * if setValue already did) and always resolves true - failure is handled by
   * the flush's own retry, not surfaced synchronously. Legacy mode: performs the
   * real upsertObservation and resolves whether it succeeded, so callers can
   * keep their existing revert-on-failure behaviour unchanged. */
  commit: (value: T, options?: { flushNow?: boolean }) => Promise<boolean>;
  /** Tombstone this field's own observation. */
  remove: () => Promise<boolean>;
  /** Tombstone a DIFFERENT path for the same entity (e.g. a checkbox's child
   * fields on uncheck). */
  removeRelated: (path: string) => Promise<boolean>;
}

const identitySerialize = (v: unknown) => v as string;
const identityDeserialize = (v: string) => v as unknown;

/**
 * Collapses the legacy-vs-draft plumbing every simple observation field atom
 * needs (dual value state, "fire onDefaultLoad once ready", commit/revert,
 * tombstone) into one hook, so atoms carry only their own UI logic. Built on
 * top of useObservationField (the low-level draft binding) - this layer adds
 * nothing new to the draft/flush contract, it only removes duplication from
 * the callers. Fields with genuinely bespoke persistence shape (RichTextField's
 * per-keystroke transform + multi-location merge) should use
 * useObservationField directly rather than force-fitting this shape.
 */
export function useObservationValue<T = string>(
  props: ObservationValueProps<T>,
): ObservationValueBinding<T> {
  const {
    defaultValue,
    onDefaultLoad,
    serialize = identitySerialize as (value: T) => string,
    deserialize = identityDeserialize as (raw: string) => T,
    ...keyProps
  } = props;

  const draft = useObservationField(keyProps);
  const isDraft = draft.mode === 'draft';

  const [legacyValue, setLegacyValue] = useState<T>(
    (defaultValue ?? deserialize('')) as T,
  );
  const [legacyReady, setLegacyReady] = useState(false);
  const defaultLoadFiredRef = useRef(false);

  // Legacy mode: fetch the persisted value once. defaultValue is a display seed
  // only - it is never written back, matching plain defaultValue conventions.
  useEffect(() => {
    if (isDraft) {
      return;
    }
    let cancelled = false;
    const load = async () => {
      const observation = await observationService.getObservation({
        dataLevel: keyProps.dataLevel,
        entityId: keyProps.entityId,
        entityExternalId: keyProps.entityExternalId,
        inspectionId: keyProps.inspectionId,
        inspectionCategoryId: keyProps.inspectionCategoryId,
        path: keyProps.path,
      });
      if (cancelled) {
        return;
      }
      const resolved = observation
        ? deserialize(
            observationService.convertValue<string>(
              observation.value,
              DataTypesEnum.string,
            ),
          )
        : (defaultValue as T);
      if (resolved !== undefined) {
        setLegacyValue(resolved);
      }
      setLegacyReady(true);
      if (!defaultLoadFiredRef.current) {
        defaultLoadFiredRef.current = true;
        if (resolved !== undefined) {
          onDefaultLoad?.(resolved);
        } else {
          onDefaultLoad?.(deserialize('') as T);
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isDraft,
    keyProps.inspectionId,
    keyProps.inspectionCategoryId,
    keyProps.entityId,
    keyProps.entityExternalId,
    keyProps.path,
    keyProps.dataLevel,
  ]);

  // Draft mode: fire onDefaultLoad exactly once, when the seed lands.
  useEffect(() => {
    if (
      !isDraft ||
      draft.mode !== 'draft' ||
      !draft.ready ||
      defaultLoadFiredRef.current
    ) {
      return;
    }
    defaultLoadFiredRef.current = true;
    const resolved =
      draft.value !== '' ? deserialize(draft.value) : (defaultValue as T);
    if (resolved !== undefined) {
      onDefaultLoad?.(resolved);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDraft, draft.mode === 'draft' && draft.ready]);

  // Legacy value ALWAYS reflects legacyValue directly (never gated on legacyReady) - it's
  // initialised to the default seed and updated by both the load effect and setValue. Gating
  // it on "ready" would make a value typed before the async initial load resolves invisible
  // to the caller (e.g. commit() reading a stale fallback instead of what was just typed) -
  // the original ungated single-state design never had this race.
  // Draft mode: a deliberate removal (tombstone) must show empty and IGNORE defaultValue -
  // otherwise a caller that seeds defaultValue from a ref (e.g. BucketGETPartCondition's
  // length.current) would have a range-guard-rejected value visually reappear. defaultValue is
  // only the pre-set placeholder; it does not apply once the field has a value or was removed.
  const value: T =
    isDraft && draft.mode === 'draft'
      ? draft.value !== ''
        ? deserialize(draft.value)
        : draft.isRemoved
          ? (deserialize('') as T)
          : ((defaultValue ?? deserialize('')) as T)
      : legacyValue;

  const setValue = (next: T) => {
    if (isDraft && draft.mode === 'draft') {
      draft.setValue(serialize(next));
    } else {
      setLegacyValue(next);
    }
  };

  const commit = async (
    explicitValue: T,
    options?: { flushNow?: boolean },
  ): Promise<boolean> => {
    if (isDraft && draft.mode === 'draft') {
      draft.setValue(serialize(explicitValue)); // idempotent if setValue already ran
      if (options?.flushNow) {
        // Blur ends the input burst: flush + invalidate promptly instead of waiting out
        // the flush debounce and then the invalidation debounce - the Action Status
        // header reads persisted observations and lagged ~1s+ behind the field
        // (measured 2s in a backgrounded tab where timers clamp). Opt-in per call site:
        // burst-driven commits (checkbox toggles) must keep the debounced batching (R7).
        //
        // Deferred by a macrotask, NOT fired inline: the caller's onChange runs the
        // range-guard validation AFTER commit, and a rejected value tombstones via the
        // validationError effect. Flushing inline would transiently persist the
        // rejected value (and could race a sync); after the deferral the flush drains
        // either the clean value or the tombstone - never the rejected value.
        setTimeout(() => {
          void observationDraftService
            .flush(keyProps.inspectionId)
            .then((ok) => {
              if (ok) {
                void cacheInvalidationService.invalidateInspectionAfterObservationChangeImmediate(
                  keyProps.inspectionId,
                );
              }
            });
        }, 0);
      }
      return true;
    }
    const response = await observationService.upsertObservation({
      dataLevel: keyProps.dataLevel,
      entityId: keyProps.entityId,
      entityExternalId: keyProps.entityExternalId,
      inspectionCategoryId: keyProps.inspectionCategoryId,
      inspectionId: keyProps.inspectionId,
      path: keyProps.path,
      value: serialize(explicitValue),
      unitId: keyProps.unitId,
      unitName: keyProps.unitName,
    });
    return response !== '';
  };

  const remove = async (): Promise<boolean> => {
    if (isDraft && draft.mode === 'draft') {
      // Self-clearing: a null draft value collapses to '' at the useObservationField layer,
      // so the display updates reactively - no separate local-state clear needed here.
      draft.remove();
      return true;
    }
    // Legacy mode has no reactive snapshot; clear the local value explicitly to match
    // draft mode's self-clearing behaviour (and the original atoms' own setValue('') call).
    const success = await observationService.deleteObservationsByProps({
      dataLevel: keyProps.dataLevel,
      entityId: keyProps.entityId,
      entityExternalId: keyProps.entityExternalId,
      inspectionId: keyProps.inspectionId,
      inspectionCategoryId: keyProps.inspectionCategoryId,
      path: keyProps.path,
      unitId: keyProps.unitId,
    });
    setLegacyValue(deserialize('') as T);
    return success;
  };

  const removeRelated = async (path: string): Promise<boolean> => {
    if (isDraft && draft.mode === 'draft') {
      draft.removePath(path);
      return true;
    }
    return observationService.deleteObservationsByProps({
      dataLevel: keyProps.dataLevel,
      entityId: keyProps.entityId,
      entityExternalId: keyProps.entityExternalId,
      inspectionId: keyProps.inspectionId,
      inspectionCategoryId: keyProps.inspectionCategoryId,
      path,
    });
  };

  return {
    value,
    ready: isDraft ? draft.mode === 'draft' && draft.ready : legacyReady,
    isDraft,
    setValue,
    commit,
    remove,
    removeRelated,
  };
}
